import {
  Step as MuiStep,
  StepConnector as MuiStepConnector,
  StepLabel as MuiStepLabel,
  Stepper as MuiStepper,
  Orientation,
  Typography,
  stepConnectorClasses,
  stepLabelClasses,
  stepperClasses,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { GearIcon } from "@phosphor-icons/react/dist/csr/Gear";
import { FC, memo } from "react";

interface FlowProgressBarProps {
  phase: string;
  failed?: boolean;
  orientation?: Orientation;
  tight?: boolean;
  hasDirtyRows?: boolean;
}

const ProgressBarContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "orientation" && prop !== "tight",
})<{
  orientation?: Orientation;
  tight?: boolean;
}>(({ orientation = "horizontal", tight }) => {
  return {
    padding: tight ? 0 : "25px 0px 25px 0px",
    display: "flex",
    justifyContent: "center",
    width: orientation === "horizontal" ? "100%" : "50%",
  };
});

const Step = styled(MuiStep)({
  "&.MuiStep-root": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
});

const IconContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "phase" && prop !== "hasDirtyRows",
})<{
  phase: string;
  hasDirtyRows?: boolean;
}>(({ phase, theme, hasDirtyRows }) => {
  return {
    height: "40px",
    width: "40px",
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "8px",
    ...(phase === "wait" && { backgroundColor: theme.palette.surfaceLight50.main }),
    ...(phase === "process" && { backgroundColor: theme.palette.accentInfo30?.main }),
    ...(phase === "error" && { backgroundColor: theme.palette.accentDanger30?.main }),
    ...(phase === "finish" && {
      backgroundColor: hasDirtyRows ? theme.palette.accentWarning30.main : theme.palette.accentSuccess30.main,
    }),
  };
});

const StepConnector = styled(MuiStepConnector, {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "hasDirtyRows",
})<{ status: string; hasDirtyRows?: boolean }>(({ theme, status, hasDirtyRows }) => ({
  "@media (min-width:991px)": {
    ...(status === "error" && {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.error.light,
        borderTopWidth: 2,
        marginBottom: 4,
        marginTop: 20,
      },
    }),
    ...((status === "wait" || status === "process") && {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.surfaceLight50.main,
        borderTopWidth: 2,
        marginBottom: 4,
        marginTop: 20,
      },
    }),
    ...(status === "finish" && {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: hasDirtyRows ? theme.palette.warning.main : theme.palette.accentSuccess20.main,
        borderTopWidth: 2,
        marginBottom: 4,
        marginTop: 20,
      },
    }),
  },

  "@media (max-width:991px)": {
    ...(status === "error" && {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.error.light,
        borderTopWidth: 2,
        marginLeft: theme.spacing(2),
      },
    }),
    ...((status === "wait" || status === "process") && {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.surfaceLight50.main,
        borderTopWidth: 2,
        marginLeft: theme.spacing(2),
      },
    }),
    ...(status === "finish" && {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: hasDirtyRows ? theme.palette.warning.main : theme.palette.accentSuccess20.main,
        borderTopWidth: 2,
        marginLeft: theme.spacing(2),
      },
    }),
  },
}));

const StepLabel = styled(MuiStepLabel)(({ theme }) => ({
  "@media (min-width:991px)": {
    [`&.${stepLabelClasses.root}`]: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
    },
  },
  [`& .${stepLabelClasses.label}`]: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text50.main,
  },
}));

const Stepper = styled(MuiStepper)({
  display: "flex",
  alignItems: "flex-start",
  [`&.${stepperClasses.root}`]: {
    width: "80%",
  },
});

const StepConnectorComponent: React.FC<{
  status: string;
  hasDirtyRows?: boolean;
}> = ({ status, hasDirtyRows }) => {
  return <StepConnector status={status} hasDirtyRows={hasDirtyRows} />;
};

const StepLabelText: FC<{
  label: string;
  isInProgress: boolean;
  testLabel: string;
  tight: boolean;
}> = ({ label, isInProgress, testLabel, tight }) => {
  return tight ? null : (
    <>
      <Typography>{label}</Typography>
      {isInProgress ? <Typography data-testid={testLabel}>In Progress</Typography> : null}
    </>
  );
};

const FlowProgressBarAux: FC<FlowProgressBarProps> = ({
  phase,
  failed = false,
  orientation = "horizontal",
  tight = false,
  hasDirtyRows = false,
}) => {
  const phaseStatus = {
    download: "wait",
    processing: "wait",
    upload: "wait",
  };
  let current = -1;

  if (phase === "downloading") {
    current = 0;
    phaseStatus.download = failed ? "error" : "process";
  } else if (phase === "processing") {
    current = 1;
    phaseStatus.download = "finish";
    phaseStatus.processing = failed ? "error" : "process";
  } else if (phase === "uploading") {
    current = 2;
    phaseStatus.download = "finish";
    phaseStatus.processing = "finish";
    phaseStatus.upload = failed ? "error" : "process";
  } else if (phase === "succeeded") {
    current = 3;
    phaseStatus.download = "finish";
    phaseStatus.processing = "finish";
    phaseStatus.upload = "finish";
  }

  const FlowDownload = ({ hasDirtyRows }: { hasDirtyRows?: boolean }) => {
    const theme = useTheme();

    return (
      <IconContainer phase={phaseStatus.download} data-testid="download-icon" hasDirtyRows={hasDirtyRows}>
        <CloudArrowDownIcon size="0.75em" width="20px" height="20px" color={theme.palette.text10.main} />
      </IconContainer>
    );
  };

  const FlowProcessing = () => {
    const theme = useTheme();
    return (
      <IconContainer phase={phaseStatus.processing}>
        <GearIcon size="0.75em" width="20px" height="20px" color={theme.palette.text10.main} />
      </IconContainer>
    );
  };

  const FlowUploading = () => {
    const theme = useTheme();
    return (
      <IconContainer phase={phaseStatus.upload}>
        <CloudArrowUpIcon size="0.75em" width="20px" height="20px" color={theme.palette.text10.main} />
      </IconContainer>
    );
  };

  return (
    <>
      {phase && (
        <ProgressBarContainer orientation={orientation} tight={tight}>
          <Stepper
            activeStep={current}
            data-test-target={"test-run-phase"}
            data-test-phase={failed ? "error" : phase}
            connector={null}
            orientation={orientation}
          >
            <Step key={"download"}>
              <StepLabel slots={{ stepIcon: () => <FlowDownload hasDirtyRows={hasDirtyRows} /> }}>
                <StepLabelText
                  label="Downloads"
                  isInProgress={phaseStatus.download === "process"}
                  testLabel="download-in-progress"
                  tight={tight}
                />
              </StepLabel>
            </Step>
            <StepConnectorComponent status={phaseStatus.download} hasDirtyRows={hasDirtyRows} />
            <Step key={"processing"}>
              <StepLabel slots={{ stepIcon: FlowProcessing }}>
                <StepLabelText
                  label="Processing"
                  isInProgress={phaseStatus.processing === "process"}
                  testLabel="process-in-progress"
                  tight={tight}
                />
              </StepLabel>
            </Step>
            <StepConnectorComponent status={phaseStatus.upload} />
            <Step key={"upload"}>
              <StepLabel slots={{ stepIcon: FlowUploading }}>
                <StepLabelText
                  label="Uploads"
                  isInProgress={phaseStatus.upload === "process"}
                  testLabel="upload-in-progress"
                  tight={tight}
                />
              </StepLabel>
            </Step>
          </Stepper>
        </ProgressBarContainer>
      )}
    </>
  );
};

const FlowProgressBar = memo(FlowProgressBarAux);

export { FlowProgressBar };
